import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { isShopifyConfigured, configureShopify } from "@/services/shopifyService";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Check, Info, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

export type Settings = {
  storeUrl?: string;
  accessToken?: string;
  apiKey?: string;
  apiSecret?: string;
  klaviyoApiKey?: string;
};

const validateSettings = (settings: Settings) => {
  // Allow partial updates - at least one field should be filled
  return Object.values(settings).some(value => value?.trim());
};

type InputField = {
  id: string;
  label: string;
  placeholder: string;
  type?: string;
  propertyName: keyof Settings;
};

const SHOPIFY_FIELDS: InputField[] = [
  {
    id: "shopify_store_url",
    label: "Shopify Store URL",
    placeholder: "https://your-store.myshopify.com",
    propertyName: "storeUrl",
  },
  {
    id: "shopify_access_token",
    label: "Access Token",
    placeholder: "shpat_xxxxxxxxxxxxxxxxxxxx",
    type: "password",
    propertyName: "accessToken",
  },
  {
    id: "shopify_api_key",
    label: "API Key",
    placeholder: "shpat_xxxxxxxxxxxxxxxxxxxx",
    type: "password",
    propertyName: "apiKey",
  },
  {
    id: "shopify_api_secret",
    label: "API Secret",
    placeholder: "shpat_xxxxxxxxxxxxxxxxxxxx",
    type: "password",
    propertyName: "apiSecret",
  },
];

const ShopifySettings = () => {
  const { toast } = useToast();
  const { brand } = useAuth();
  const [settings, setSettings] = useState({
    storeUrl: brand?.shopify_store_url || "",
    accessToken: brand?.shopify_access_token || "",
    apiKey: brand?.shopify_api_key || "",
    apiSecret: brand?.shopify_api_secret || "",
  });
  const [isConfigured, setIsConfigured] = useState(isShopifyConfigured());
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");

  const handleSaveSettings = () => {
    if (!validateSettings(settings)) {
      toast({
        title: "No changes to save",
        description: "Please fill in at least one field to save.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Only save fields that have values
      const settingsToSave = Object.entries(settings).reduce((acc, [key, value]) => {
        if (value?.trim()) {
          acc[key as keyof Settings] = value.trim();
        }
        return acc;
      }, {} as Settings);

      // Call configureShopify which will save to localStorage and reload the page
      configureShopify(settingsToSave);

      // The page will reload, so the below code won't actually run
      setIsConfigured(true);
      setIsSaving(false);
      toast({
        title: "Settings saved",
        description: "Shopify API settings have been saved successfully.",
      });
    } catch (error) {
      setIsSaving(false);
      toast({
        title: "Error saving settings",
        description: "There was a problem saving your Shopify API settings.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const field = SHOPIFY_FIELDS.find((f) => f.id === id);

    if (field) {
      setSettings((prev) => ({
        ...prev,
        [field.propertyName]: value,
      }));
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Shopify API Settings</CardTitle>
        <CardDescription>Configure your Shopify API credentials to enable discount code generation</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="implementation">Implementation Guide</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-4">
            {SHOPIFY_FIELDS.map((field) => (
              <div key={field.id} className="space-y-2">
                <Label htmlFor={field.id}>{field.label}</Label>
                <Input
                  id={field.id}
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  value={settings[field.propertyName]}
                  onChange={handleInputChange}
                />
                <p className="text-sm text-muted-foreground">Enter your {field.label.toLowerCase()}</p>
              </div>
            ))}
            {isConfigured && (
              <div className="rounded-md bg-green-50 p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      Connected to: {localStorage.getItem("SHOPIFY_STORE_NAME") || settings.storeUrl}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="implementation" className="space-y-4">
            <div className="rounded-md bg-amber-50 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">Backend Implementation Required</h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      To make real Shopify API calls, you need to set up a backend service. This is necessary because:
                    </p>
                    <ul className="list-disc ml-5 mt-2">
                      <li>Browsers prevent direct API calls to Shopify due to CORS restrictions</li>
                      <li>Your Shopify API credentials need to be kept secure (not exposed in frontend code)</li>
                      <li>Shopify API rate limits are better managed from a backend service</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium">Step-by-Step Integration Guide</h3>

              <div className="space-y-5 mt-4">
                <div className="p-4 border rounded-md">
                  <h4 className="font-medium flex items-center">
                    <span className="flex items-center justify-center rounded-full bg-blue-100 text-blue-600 w-6 h-6 mr-2 text-sm">
                      1
                    </span>
                    Create a Private App in Shopify
                  </h4>
                  <ol className="ml-8 mt-2 space-y-1 text-sm list-decimal">
                    <li>Log in to your Shopify admin panel</li>
                    <li>
                      Go to <strong>Apps and sales channels</strong> &gt; <strong>Develop apps</strong> &gt;{" "}
                      <strong>Create an app</strong>
                    </li>
                    <li>Give your app a name (e.g., "Photo Discount Generator")</li>
                    <li>
                      Set appropriate <strong>Admin API scopes</strong>:
                      <ul className="list-disc ml-5 mt-1">
                        <li>price_rules: read_price_rules, write_price_rules</li>
                        <li>discounts: read_discounts, write_discounts</li>
                      </ul>
                    </li>
                    <li>Install the app in your store</li>
                    <li>
                      Copy the <strong>Admin API access token</strong>
                    </li>
                  </ol>
                </div>

                <div className="p-4 border rounded-md">
                  <h4 className="font-medium flex items-center">
                    <span className="flex items-center justify-center rounded-full bg-blue-100 text-blue-600 w-6 h-6 mr-2 text-sm">
                      2
                    </span>
                    Create a Backend Service
                  </h4>
                  <p className="ml-8 mt-2 text-sm">Choose one of these options:</p>
                  <div className="ml-8 mt-2 space-y-3">
                    <div className="p-3 bg-gray-50 rounded-md">
                      <h5 className="font-medium text-sm">Option A: NodeJS Server</h5>
                      <p className="text-xs mt-1">Create an Express server with endpoints for Shopify operations</p>
                      <a
                        href="https://github.com/Shopify/shopify-node-api"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs flex items-center text-blue-600 hover:underline mt-1"
                      >
                        Shopify Node API Library <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-md">
                      <h5 className="font-medium text-sm">Option B: Serverless Functions</h5>
                      <p className="text-xs mt-1">Deploy serverless functions on Vercel, Netlify, or AWS Lambda</p>
                      <div className="flex mt-1 space-x-3">
                        <a
                          href="https://vercel.com/docs/functions/serverless-functions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs flex items-center text-blue-600 hover:underline"
                        >
                          Vercel <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                        <a
                          href="https://docs.netlify.com/functions/overview/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs flex items-center text-blue-600 hover:underline"
                        >
                          Netlify <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-md">
                  <h4 className="font-medium flex items-center">
                    <span className="flex items-center justify-center rounded-full bg-blue-100 text-blue-600 w-6 h-6 mr-2 text-sm">
                      3
                    </span>
                    Implement Backend Endpoints
                  </h4>
                  <div className="ml-8 mt-2 space-y-2 text-sm">
                    <p>Create these API endpoints in your backend:</p>
                    <ol className="list-decimal ml-5 space-y-1">
                      <li>
                        <code>/api/price-rules</code> - Creates a Shopify price rule
                      </li>
                      <li>
                        <code>/api/discount-codes</code> - Creates a discount code for a price rule
                      </li>
                      <li>
                        <code>/api/send-email</code> - Sends the discount code to the customer
                      </li>
                    </ol>
                    <p className="text-xs mt-2">
                      Your backend should securely store the Shopify credentials and make the actual API calls to
                      Shopify.
                    </p>
                  </div>
                </div>

                <div className="p-4 border rounded-md">
                  <h4 className="font-medium flex items-center">
                    <span className="flex items-center justify-center rounded-full bg-blue-100 text-blue-600 w-6 h-6 mr-2 text-sm">
                      4
                    </span>
                    Connect Frontend to Backend
                  </h4>
                  <div className="ml-8 mt-2 space-y-2 text-sm">
                    <p>Update the frontend code:</p>
                    <ol className="list-decimal ml-5 space-y-1">
                      <li>
                        Set <code>BACKEND_API_URL</code> in <code>shopifyService.ts</code> to your backend URL
                      </li>
                      <li>Implement actual API calls to your backend service instead of mock code</li>
                    </ol>
                    <p className="text-xs mt-2 text-gray-600">
                      The shopifyService.ts file already has placeholders for backend API calls.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter>
        {activeTab === "settings" && (
          <Button onClick={handleSaveSettings} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        )}
        {activeTab === "implementation" && (
          <Button variant="outline" onClick={() => setActiveTab("settings")}>
            Back to Settings
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default ShopifySettings;
